/* Precision 2026: small progressive enhancements; commerce remains theme-native. */
(() => {
  'use strict';
  if (window.SenfinecoPrecision26) return;
  const root = document.documentElement;
  const initialized = new WeakSet(), observedContainers = new WeakSet();
  const tabControllers = new WeakMap();
  let preview = null, previewResize = null, previewAttributes = null, frame = 0;
  let viewportBaseline = innerHeight;
  const setPx = (name, value) => {
    const next = `${Math.max(0, Math.round(value))}px`;
    if (root.style.getPropertyValue(name) !== next) root.style.setProperty(name, next);
  };
  function measure() {
    frame = 0;
    const v = window.visualViewport;
    const bottom = v ? v.height + v.offsetTop : innerHeight;
    const normal = !v || Math.abs(v.scale - 1) < .02;
    const inset = normal ? Math.max(0, innerHeight - bottom) : 0;
    const editable = document.activeElement?.matches('input:not([type=radio]):not([type=checkbox]),textarea,[contenteditable=true]');
    if (!editable) viewportBaseline = innerHeight;
    document.body.toggleAttribute('data-sf26-keyboard', Boolean(innerWidth <= 1024 && editable && (inset > 120 || viewportBaseline - (v?.height || innerHeight) > 120)));
    let height = 0;
    if (preview?.isConnected) {
      const rect = preview.getBoundingClientRect(), style = getComputedStyle(preview);
      if (style.display !== 'none' && style.visibility !== 'hidden' && Number(style.opacity) > 0) height = Math.max(0, Math.min(rect.bottom, bottom) - Math.max(rect.top, v?.offsetTop || 0));
    }
    setPx('--sf-preview-bar-height', height);
    setPx('--sf26-inset', inset);
  }
  const schedule = () => { if (!frame) frame = requestAnimationFrame(measure); };
  function bindPreview() {
    const bar = document.getElementById('PBarNextFrameWrapper');
    if (bar !== preview) {
      previewResize?.disconnect(); previewAttributes?.disconnect(); preview = bar;
      if (bar) {
        if ('ResizeObserver' in window) { previewResize = new ResizeObserver(schedule); previewResize.observe(bar); }
        previewAttributes = new MutationObserver(schedule);
        previewAttributes.observe(bar, {attributes:true, attributeFilter:['style','hidden','class']});
      }
    }
    schedule();
  }
  function tabs(section) {
    if (initialized.has(section)) return;
    const list = section.querySelector('[data-sf26-tablist]');
    const links = [...section.querySelectorAll('[data-sf26-tab]')];
    const panels = links.map(link => document.getElementById(link.hash.slice(1)));
    if (!list || !links.length || panels.some(panel => !panel)) return;
    initialized.add(section);
    list.setAttribute('role','tablist');
    const memoryKey = `sf26:department:${section.id}`;
    const choose = (index, remember = false) => {
      if (!Number.isInteger(index) || index < 0 || index >= links.length) return;
      links.forEach((link,i) => {
        link.setAttribute('aria-selected', String(i === index)); link.tabIndex = i === index ? 0 : -1;
        panels[i].hidden = i !== index;
      });
      if (remember) {
        try { sessionStorage.setItem(memoryKey, links[index].hash); } catch (_) { /* Storage is optional. */ }
        try { const url = new URL(location.href); url.hash = links[index].hash; history.replaceState(history.state, '', url); } catch (_) { /* Tabs still work. */ }
      }
    };
    links.forEach((link,i) => {
      link.setAttribute('role','tab'); link.setAttribute('aria-controls',panels[i].id);
      panels[i].setAttribute('role','tabpanel'); panels[i].setAttribute('aria-labelledby',link.id); panels[i].tabIndex = 0;
      link.addEventListener('click', event => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0) return;
        event.preventDefault(); choose(i,true);
      });
      link.addEventListener('keydown',event => {
        if (event.key === ' ') { event.preventDefault(); choose(i,true); return; }
        const direction = getComputedStyle(list).direction === 'rtl' ? -1 : 1;
        let next;
        if (event.key === 'ArrowRight') next = (i + direction + links.length) % links.length;
        if (event.key === 'ArrowLeft') next = (i - direction + links.length) % links.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = links.length - 1;
        if (next === undefined) return;
        event.preventDefault(); choose(next,true); links[next].focus();
      });
    });
    let remembered = '';
    try { remembered = sessionStorage.getItem(memoryKey) || ''; } catch (_) { /* Private mode. */ }
    let requested = links.findIndex(link => link.hash === location.hash);
    if (requested < 0) requested = links.findIndex(link => link.hash === remembered);
    choose(requested < 0 ? 0 : requested);
    tabControllers.set(section, () => { const index = links.findIndex(link => link.hash === location.hash); if(index >= 0) choose(index); });
    section.classList.add('sf26-tabs-ready');
  }
  function count(value) {
    if (!Number.isFinite(value) || value < 0) return;
    document.querySelectorAll('[data-sf26-count]').forEach(badge => { badge.textContent = value > 99 ? '99+' : String(value); badge.hidden = value === 0; });
    document.querySelectorAll('[data-sf26-cart-label]').forEach(link => link.setAttribute('aria-label', `${link.dataset.sf26CartLabel} (${value})`));
  }
  function alignCards() {
    document.querySelectorAll('#product-grid .product-card__information').forEach(info => {
      const row = info.querySelector(':scope > .sf-card-buy-row');
      if (!row || row.querySelector('.price')) return;
      const price = [...info.children].find(el => el !== row && (el.matches('.price') || el.querySelector(':scope > .price')));
      if (price) row.prepend(price);
    });
  }
  function removeFloatingClutter() {
    document.querySelectorAll('a[href*="wa.me"],a[href*="api.whatsapp.com"],a[href*="whatsapp.com/send"]').forEach(link => {
      if (link.closest('.sf26-footer,cart-drawer,.sf-oil-dialog,.sf-native-nav__panel')) return;
      let node = link;
      for (let depth=0; node && node!==document.body && depth<4; depth++,node=node.parentElement) {
        if (getComputedStyle(node).position === 'fixed') { node.classList.add('sf26-floating-help'); break; }
      }
    });
  }
  function bindGrid() {
    const grid = document.getElementById('product-grid');
    if (!grid) return;
    const container = grid.closest('#ProductGridContainer,.product-grid-container') || grid.parentElement;
    if (!container || observedContainers.has(container)) return;
    observedContainers.add(container);
    let pending = false;
    new MutationObserver(() => { if (pending) return; pending = true; requestAnimationFrame(() => {pending=false;alignCards();}); }).observe(container,{childList:true,subtree:true});
  }
  function refresh() { document.querySelectorAll('[data-sf26-tabs]').forEach(tabs); alignCards(); bindGrid(); bindPreview(); removeFloatingClutter(); }
  function ready() {
    refresh();
    new MutationObserver(() => {bindPreview();removeFloatingClutter();}).observe(document.body,{childList:true});
    const header = document.getElementById('cart-icon-bubble');
    if (header) new MutationObserver(() => { const text=header.querySelector('.cart-count-bubble [aria-hidden=true]')?.textContent; if(text!==undefined)count(Number(text.trim())); }).observe(header,{childList:true,subtree:true,characterData:true});
    document.addEventListener('cart:updated',event => count(Number(event.detail?.cart?.item_count)));
    document.addEventListener('shopify:section:load',refresh);
    document.addEventListener('sf:inline-products-updated',refresh);
    document.addEventListener('focusin',schedule,{passive:true});
    document.addEventListener('focusout',() => setTimeout(schedule,80),{passive:true});
    for(const name of ['resize','orientationchange','pageshow'])window.addEventListener(name,refresh,{passive:true});
    window.addEventListener('hashchange', () => document.querySelectorAll('[data-sf26-tabs]').forEach(section => tabControllers.get(section)?.()));
    window.addEventListener('load',refresh,{once:true});
    window.visualViewport?.addEventListener('resize',schedule,{passive:true});
    window.visualViewport?.addEventListener('scroll',schedule,{passive:true});
  }
  window.SenfinecoPrecision26 = {version:'1.1.0',refresh};
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',ready,{once:true}); else ready();
})();
